import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trophy, Crown, Star } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

export default function Leaderboard() {
    const { user } = useAuth();
    const [data, setData] = useState({ leaderboard: [], your_rank: 0 });

    useEffect(() => {
        api.get("/leaderboard").then((r) => setData(r.data)).catch(() => {});
    }, []);

    const yourRank = data.your_rank || 0;
    const nextTier = Math.max(1, ((Math.ceil(yourRank / 100) - 1)) * 100 || 1);
    const spotsToNext = Math.max(0, yourRank - nextTier);

    return (
        <section data-testid="leaderboard-section" className="bg-[#0A0A0A] border border-white/10 relative overflow-hidden h-full flex flex-col">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Trophy size={20} className="text-electric-blue" weight="duotone" />
                    <div>
                        <p className="label-mono text-electric-blue">CREATOR STANDINGS</p>
                        <h2 className="font-heading font-bold text-lg">The Top 10</h2>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto scrollbar-thin">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[#0A0A0A]">
                        <tr className="border-b border-white/10">
                            <th className="text-left px-4 py-3 label-mono text-[10px]">#</th>
                            <th className="text-left px-2 py-3 label-mono text-[10px]">OPERATOR</th>
                            <th className="text-right px-4 py-3 label-mono text-[10px]">PTS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.leaderboard.map((row, i) => (
                            <tr
                                key={row.rank}
                                data-testid={`leaderboard-row-${i}`}
                                className={`border-b border-white/5 transition-colors ${
                                    row.is_placeholder ? "opacity-40" : "hover:bg-electric-blue/5"
                                } ${row.display_name === user?.display_name ? "bg-electric-blue/10" : ""}`}
                            >
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1.5">
                                        {row.rank === 1 && <Crown size={14} weight="fill" className="text-yellow-400" />}
                                        <span className={`font-mono ${row.rank <= 3 ? "text-electric-blue font-bold" : ""}`}>
                                            {row.rank}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-2 py-3 truncate max-w-[140px]">
                                    <div className="flex items-center gap-1.5">
                                        {row.lifetime_member && (
                                            <span title="Lifetime Founder" data-testid={`leaderboard-lifetime-${i}`}>
                                                <Star size={11} weight="fill" className="text-yellow-400 flex-shrink-0" />
                                            </span>
                                        )}
                                        <span className={`truncate ${row.is_placeholder ? "italic" : "font-medium"} ${row.lifetime_member ? "text-yellow-200" : ""}`}>
                                            {row.display_name}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-right font-mono">{row.points}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Personalized Rank Bar */}
            <div data-testid="leaderboard-personal-rank" className="px-5 py-4 border-t border-electric-blue/30 bg-electric-blue/5">
                <p className="label-mono text-electric-blue mb-1">YOUR POSITION</p>
                <p className="text-sm">
                    Your Rank: <span className="font-bold text-electric-blue">#{yourRank || "—"}</span>
                    {spotsToNext > 0 && (
                        <span className="text-muted-foreground">
                            {" "}
                            | {spotsToNext} spots away from{" "}
                            <span className="text-white">Bronze Tier!</span>
                        </span>
                    )}
                </p>
            </div>
        </section>
    );
}
